/**
 * Display helpers for connector / firmware / security status enums.
 *
 * Pure functions only — no React, no platform APIs. Tested in
 * `src/ocpp/__tests__/statusFormat.test.ts`.
 */

import type { ConnectorStatus } from '../models/connectors';
import type { FirmwareStatus } from '../models/firmware';
import type { SecurityEventSeverity, SecurityOpState } from '../models/security';
import type { OcppReservationStatus } from '../models/reservations';
import type { DataQuality } from '../models/meter';

/** Theme palette pulled from the existing design system (see `charger-model.tsx`). */
export const OCPP_COLORS = {
  green: '#51B868',
  teal: '#39BEB7',
  blue: '#3B82F6',
  amber: '#F59E0B',
  orange: '#FF9500',
  red: '#EF4444',
  darkRed: '#DC2626',
  purple: '#8B5CF6',
  gray: '#6B7280',
  dim: '#8D8E90',
} as const;

export const connectorStatusColor = (status: ConnectorStatus | string | null | undefined): string => {
  switch (status) {
    case 'Available':
      return OCPP_COLORS.green;
    case 'Preparing':
      return OCPP_COLORS.blue;
    case 'Charging':
      return OCPP_COLORS.teal;
    case 'SuspendedEV':
    case 'SuspendedEVSE':
      return OCPP_COLORS.amber;
    case 'Finishing':
      return OCPP_COLORS.purple;
    case 'Reserved':
      return OCPP_COLORS.amber;
    case 'Faulted':
      return OCPP_COLORS.red;
    case 'Unavailable':
      return OCPP_COLORS.gray;
    case 'Occupied':
      return OCPP_COLORS.teal;
    default:
      return OCPP_COLORS.gray;
  }
};

export const connectorStatusLabel = (status: ConnectorStatus | string | null | undefined): string => {
  if (!status) return 'Unknown';
  return status
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
};

export const firmwareStatusColor = (status: FirmwareStatus | string | null | undefined): string => {
  if (!status) return OCPP_COLORS.gray;
  if (status.endsWith('Failed')) return OCPP_COLORS.red;
  if (status === 'Installed' || status === 'InstallVerified') return OCPP_COLORS.green;
  if (status === 'Idle') return OCPP_COLORS.dim;
  if (status.startsWith('Install')) return OCPP_COLORS.purple;
  if (status.startsWith('Download')) return OCPP_COLORS.blue;
  return OCPP_COLORS.amber;
};

export const securitySeverityColor = (severity: SecurityEventSeverity | string | null | undefined): string => {
  switch (severity) {
    case 'critical':
      return OCPP_COLORS.darkRed;
    case 'warning':
      return OCPP_COLORS.amber;
    case 'info':
      return OCPP_COLORS.blue;
    default:
      return OCPP_COLORS.gray;
  }
};

export const securityOpStateLabel = (state: SecurityOpState | string): string => {
  return state.replace(/_/g, ' ');
};

export const reservationStatusColor = (status: OcppReservationStatus | string): string => {
  switch (status) {
    case 'active':
      return OCPP_COLORS.teal;
    case 'fulfilled':
      return OCPP_COLORS.green;
    case 'expired':
    case 'cancelled':
      return OCPP_COLORS.gray;
    case 'occupied':
    case 'unavailable':
    case 'faulted':
    case 'rejected':
      return OCPP_COLORS.red;
    default:
      return OCPP_COLORS.gray;
  }
};

/**
 * Settlement guard: rows tagged `suspect` MUST NOT auto-settle. The session
 * detail screen branches off this. See §4.8 in the backend doc.
 */
export const isBlockingDataQuality = (q: DataQuality | string | null | undefined): boolean => {
  return q === 'suspect';
};

export const dataQualityColor = (q: DataQuality | string | null | undefined): string => {
  switch (q) {
    case 'clean':
      return OCPP_COLORS.green;
    case 'interpolated':
      return OCPP_COLORS.amber;
    case 'recovered':
      return OCPP_COLORS.orange;
    case 'suspect':
      return OCPP_COLORS.red;
    default:
      return OCPP_COLORS.gray;
  }
};

/** ISO datetime → friendly local string. Tolerant of bad input. */
export const formatOcppTimestamp = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};
